import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  styleMap,
} from '@lit'

import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { formatMoney, handleize } from '@nova/utils'

import { addToCart } from '@nova/utils'

const quickShopState = {
  initial: 'SHOW_OPTION_SELECTOR',
  submit: 'SHOW_ADD_TO_CART',
  loading: 'SHOW_LOADING',
  confirm: 'SHOW_CONFIRMATION',
  error: 'ERROR',
}

class NovaProductCard extends LitElement {
  static properties = {
    url: {
      type: String,
    },
    available: {
      type: Boolean,
    },
    title: {
      type: String,
    },
    subtitle: {
      type: String,
    },
    image: {
      type: String,
      attribute: 'image',
    },
    imageAlt: {
      type: String,
    },
    secondaryImage: {
      type: String,
      attribute: 'secondary-image',
    },
    secondaryImageAlt: {
      type: String,
    },
    compareAtPrice: {
      type: Number,
      attribute: 'compare-at-price',
    },
    swatches: {
      type: Array,
    },
    colorValues: {
      type: Array,
    },
    sizes: {
      type: Array,
    },
    price: {
      type: Number,
    },
    isPriceVaries: {
      type: Boolean,
      attribute: 'price-varies',
      converter: (value) => value === "true",
    },
    vendor: {
      type: String,
    },
    variants: {
      type: Array,
    },
    badge: {
      type: Object,
    },
    activeIndex: {
      type: Number,
    },
    options: {
      type: Array,
    },
    hideRatings: {
      attribute: 'hide-ratings',
    },
    showWishlist: {
      type: Boolean,
      attribute: 'show-wishlist',
      converter: (value) => value === "true",
    },
    quickShop: {
      type: Boolean,
      attribute: 'quick-shop',
      converter: (value) => value === "true",
    },
    hideSwatches: {
      state: true,
    },
    isHovered: {
      state: true,
    },
    isMobile: {
      state: true,
    },
    slidesForCarousel: {
      type: Number,
      state: true,
    },
    quickShopVariant: {
      type: Object,
      state: true,
    },
    status: {
      type: String,
      state: true,
    },
    variantId: {
      attribute: 'variant-id',
    },
    images: {
      type: Array,
    },
    inCollection: {
      type: Boolean,
      attribute: 'in-collection',
      converter: (value) => value === "true",
    },
    hidePrice: {
      type: Boolean,
      attribute: 'hide-price',
      converter: (value) => value === "true",
    },
    unit: {
      type: String,
    }
  }

  $productCardContentRef = createRef()

  get activeTitle() {
    let title = this.swatchVariants[this.activeIndex]?.name?.split('-')[0]?.trim() || ''
    if(title) title += ` - ${this.swatchVariants[this.activeIndex]?.option1}`
    return title || this.title
  }

  get activeId() {
    return this.swatchVariants[this.activeIndex]?.id || this.variantId
  }

  get activePrice() {
    if (this.quickShopVariant) return formatMoney(this.quickShopVariant.price)

    if (!this.swatchVariants.length) return formatMoney(this.price)

    return formatMoney(this.swatchVariants[this.activeIndex]?.price)
  }

  get activeAvailable() {
    if (this.quickShopVariant) return this.quickShopVariant.available

    if (this.swatchVariants[this.activeIndex]) {
      return this.swatchVariants[this.activeIndex]?.available
    }
    return this.available
  }

  get activeImage() {
    // non-attached images to variant
    const swatchProductImages = this.images[this.activeIndex]?.filter((item) => !item[1])
    let image =
      this.swatchVariants[this.activeIndex]?.featured_image?.src ||
      (swatchProductImages && swatchProductImages.length > 0 && swatchProductImages[0][0]) ||
      this.image ||
      ''

    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x'),
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_480x`
      image = fragments.join('.')
    }

    return `${image}&width=480&height=640`
  }

  get activeSecondaryImage() {
    // non-attached images to variant
    const swatchProductImages = this.images[this.activeIndex]?.filter((item) => !item[1])
    let secondaryProductImage = swatchProductImages && swatchProductImages.length > 1 ? swatchProductImages[1][0]: ''
    if (this.swatchVariants[this.activeIndex]?.featured_image?.src) {
      secondaryProductImage = swatchProductImages && swatchProductImages.length ? swatchProductImages[0][0]: ''
    }
    let image =
      secondaryProductImage ||
      ''
    if(!image) return ''
    const isSmall = image
      .split('_')
      .some(
        (fragment) => fragment.includes('x480') || fragment.includes('480x'),
      )

    if (!isSmall) {
      const fragments = image.split('.')
      fragments[fragments.length - 2] = `${
        fragments[fragments.length - 2]
      }_480x`
      image = fragments.join('.')
    }

    return `${image}&width=480&height=640`
  }

  get activeUrl() {
    const variantId = this.swatchVariants.length > 0
      ? this.swatchVariants[this.activeIndex]?.id
      : this.variantId
    return variantId ? `${this.url}?variant=${variantId}` : this.url
  }

  get allVariantsBySwatch() {
    const swatch = this.swatchVariants[this.activeIndex]?.swatchTitle

    return this.variants.filter((variant) => variant.options?.includes(swatch))
  }

  get activeCompareAtPrice() {
    if (this.unit !== '') return false

    if (this.variants?.length > 1) {
      const activeVariant =
        this.quickShopVariant || this.swatchVariants[this.activeIndex]
      const _activeCompareAtPrice = Number.parseFloat(
        activeVariant?.compare_at_price,
      )
      const _activePrice = Number.parseFloat(activeVariant?.price)

      if (!_activeCompareAtPrice) return false

      if (_activeCompareAtPrice > _activePrice) {
        return formatMoney(
          this.swatchVariants[this.activeIndex]?.compare_at_price,
        )
      }
    } else {
      if (!this.compareAtPrice) return false

      if (this.compareAtPrice > this.price)
        return formatMoney(this.compareAtPrice)
    }

    return false
  }

  constructor() {
    super()
    this.activeIndex = 0
    this.variants = []
    this.swatches = []
    this.images = []
    this.options = []
    this.swatchVariants = []
    this.swatchesPosition = 0
    this.swatchesPerSlide = 5
    this.activeSwatchPage = 0
    this.slidesForCarousel = 1
    this.isHovered = false
    this.isMobile = false
    this.status = quickShopState.initial
    this.hideRatings = window.Shopify.shop.includes('wholesale') // Hide ratings by default on wholesale store
  }

  connectedCallback() {
    super.connectedCallback()
    window.addEventListener('nova:resize-width', this.handleResize)
  }

  disconnectedCallback() {
    window.removeEventListener('nova:resize-width', this.handleResize)
    this.productLinks.forEach(productLink => {
      productLink.removeEventListener('click', this.handleProductLinkClick.bind(this))
    })
  }

  async firstUpdated() {
    this.setSwatchVariants()
    if (this.swatches?.length) {
      this.setActiveByVariantId(this.variantId, true)
    }
    this.handleResize()

    this.productLinks = [...this.shadowRoot.querySelectorAll('.js-product-link')]
    this.productLinks.forEach(productLink => {
      productLink.addEventListener('click', this.handleProductLinkClick.bind(this))
    })
  }

  updated(changedProperties) {
    if (changedProperties.has('variants')) {
      this.setSwatchVariants()
    }
    if (changedProperties.has('activeIndex')) {
      this.requestUpdate()
    }

    if (changedProperties.has('activeImage')) {
      this.requestUpdate()
    }
    if (changedProperties.has('activeSecondaryImage')) {
      this.requestUpdate()
    }
  }

  handleProductLinkClick(event) {
    event.preventDefault()
    const activeLink = event.target.closest('.js-product-link')
    const id = activeLink.dataset.id
    const params = new URLSearchParams(location.search)
    const newQueryString = params.toString()
    history.replaceState(
      {newQueryString},
      '',
      `?${newQueryString}#${id}`,
    )

    location.href = activeLink.getAttribute('href')
  }

  handleOptionSelection(event) {
    this.status = quickShopState.submit
    this.quickShopVariant = event.detail.variant
  }

  setSwatchVariants() {
    if (!this.swatches || !this.variants.length) return []
    let nonColorOptions
    if (this.options?.length) {
      nonColorOptions = this.options.filter(
        (option) => !this.swatches.includes(option),
      )
    }

    const matches = this.swatches.reduce((accumulator, swatch) => {
      let match
      if (this.options?.length) {
        match = this.variants?.filter((variant) => {
          for (const option of nonColorOptions) {
            if (!variant?.options.includes(option)) return false
          }
          return variant?.options.includes(swatch)
        })
      } else {
        match = this.variants?.filter((variant) =>
          variant?.options.includes(swatch),
        )
      }

      if (match.length) {
        match.sort((a, b) => a.price - b.price)
        accumulator.push(match[0])
      }
      return accumulator
    }, [])
    this.swatchVariants = matches
  }

  setSlidesForCarousel() {
    const container = this.$productCardContentRef.value

    if (this.isMobile || !container) {
      this.slidesForCarousel = 1
      return
    }

    const styles = getComputedStyle(container)
    const totalWidth =
      container.clientWidth -
      Number.parseFloat(styles.paddingLeft) -
      Number.parseFloat(styles.paddingRight)
    // Swatch size: var(--nova-product-color-swatch-size-active) = 25px over 768px screen width
    // Swatch gap: var(--nova-space-xs) = 10px over 768px screen width
    const slidesToShow = Math.floor(totalWidth / (25 + 10))
    this.slidesForCarousel = slidesToShow >= 1 ? slidesToShow : 1
  }

  handleSwatchClick(event) {
    const { variantId } = event.target.dataset
    this.quickShopVariant = null

    if (!variantId) return

    this.status = quickShopState.initial
    this.setActiveByVariantId(variantId)
  }

  handleResize = () => {
    this.isMobile = window.innerWidth <= 768
    this.setSlidesForCarousel()
  }

  handleAddToWishlist(e) {
    const { variantId } = e.target.dataset

    if (!variantId) return

    alert(variantId)
  }

  handleMouseEnter() {
    this.isHovered = true
  }

  handleMouseLeave() {
    this.isHovered = false
  }

  setActiveIndex(index) {
    this.activeIndex = index
  }

  setActiveByVariantId(id, setPage = false) {
    const variantIndex = this.swatchVariants.findIndex(
      (variant) => variant?.id === Number.parseInt(id),
    )

    if (variantIndex > -1) {
      this.setActiveIndex(variantIndex)
    }

    if (setPage) {
      const pageNumber = this.paginatedSwatches?.findIndex((page) =>
        page.some((variant) => variant.id === Number.parseInt(id)),
      )
      if (pageNumber > -1 && pageNumber !== this.activeSwatchPage) {
        this.setSwatchPage(pageNumber)
      }
    }
  }

  findVariantBySwatch(swatch) {
    return this.swatchVariants?.find((variant) => variant?.options.includes(swatch))
  }

  sanitizeString(string) {
    return string
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
  }

  async handleAddToCartClick(e) {
    this.status = quickShopState.loading
    try {
      const variantId = e.target.dataset.variantId
      await addToCart({ id: variantId })
      this.status = quickShopState.confirm
    } catch (error) {
      this.status = quickShopState.error
    }
  }

  renderMedia() {
    return html`
      <div class="product-card__media ${classMap({
        'product-card__media--enable-hover': this.activeSecondaryImage,
      })}">
        <div class="product-card__image product-card__image--primary">
          <a href=${this.activeUrl} class="js-product-link" data-id="${this.id}">
            ${
              this.image
                ? html`<img src=${this.activeImage} alt=${this.title} loading="lazy" width="480px" height="640px"/>`
                : paths.placeholderImage
            }
          </a>
        </div>

        ${
          this.activeSecondaryImage
            ? html`
            <div class="product-card__image product-card__image--secondary">
              <a href=${this.activeUrl} class="js-product-link" data-id="${this.id}">
                <img src=${this.activeSecondaryImage} alt=${this.title} loading="lazy" width="480px" height="640px"/>
              </a>
            </div>`
            : nothing
        }

        ${this.renderBadges()}
        ${this.showWishlist && this.isHovered ? this.renderWishlist() : nothing}
      </div>`
  }

  renderBadges() {
    if (!this.badge) return null

    let styleVars = {}
    if (this.badge.colour && this.badge.font_colour) {
      styleVars = {
        '--badge-bg-color': this.badge.colour,
        '--badge-color': this.badge.font_colour,
      }
    }

    return html`
      <div class="badges">
        <span class="badges__item caption" style=${styleMap(styleVars)}>
          ${this.badge.label}
        </span>
      </div>
    `
  }

  renderWishlist() {
    return html`
      <sl-button
        class="product-card__wishlist"
        variant="text"
        size="small"
        aria-label="Add Product To Wishlist"
        data-variant-id=${this.activeId}
        @click=${this.handleAddToWishlist}>
        ${paths.heart}
        <span class="visually-hidden">Add Product To Wishlist</span>
      </sl-button>
    `
  }

  renderPrice() {
    return html`
      <span class="product-card__price caption flex">
        <div class="price ${classMap({
          'price--on-sale': this.activeCompareAtPrice,
        })}">
          <dl>
            <div class="price__regular">
              <dt>
                <span class="visually-hidden visually-hidden--inline">Regular price</span>
              </dt>
              <dd>
                <span class="price-item price-item--regular">
                  ${this.activePrice}
                  ${this.unit !== '' ? html` / ${this.unit}` : nothing}
                </span>
              </dd>
            </div>

            ${
              this.activeCompareAtPrice
                ? html`
              <div class="price__sale ">
                <dt>
                  <span class="visually-hidden visually-hidden--inline">Sale price</span>
                </dt>
                <dd>
                  <span class="price-item price-item--sale">
                    ${this.activeCompareAtPrice}
                  </span>
                </dd>
                <dt class="price__compare">
                  <span class="visually-hidden visually-hidden--inline">Regular price</span>
                </dt>
                <dd class="price__compare">
                  <s class="price-item price-item--regular">
                    ${this.activePrice}
                  </s>
                </dd>
              </div>
              `
                : nothing
            }
          </dl>
        </div>
      </span>
    `
  }

  renderSwatch = (swatch, index) => {
    const swatchSanitize = this.sanitizeString(swatch)
    const variant = this.findVariantBySwatch(swatch)
    if (variant) {
      const stateClasses = {
        'product-card__swatch--active': variant.id === Number(this.activeId),
        'product-card__swatch--sold-out': !variant.available,
      }

      return html`
        <button
          class="product-card__swatch ${classMap(stateClasses)}"
          data-variant-id=${variant.id}
          aria-label=${swatch}
          @click=${this.handleSwatchClick}>
            <span style="background: ${this.colorValues[index]};">${swatchSanitize}</span>
        </button>
      `
    }
    return null
  }

  renderProductSwatches() {
    if (!this.swatches) return nothing

    const swatchesEl = this.swatches.map(this.renderSwatch)
    return html`
      <div class="product-card__swatch-container">
        <div class="product-card__swatch-wrapper">
          ${swatchesEl}
        </div>
        ${this.renderProductSwatchesMore(swatchesEl)}
      </div>
    `
  }

  renderProductSwatchesMore(swatchesEl) {
    const limitedSwatchCount = 3
    return html`
      ${
        swatchesEl.length > limitedSwatchCount
          ? html`<span class="product-card__swatch-container__trigger"><a href=${this.url}>+${swatchesEl.length - limitedSwatchCount}</a></span>`
          : ''
      }
    `
  }

  renderQuickShopCTAs() {
    if (
      !this.isHovered ||
      !this.quickShopVariant ||
      this.status === quickShopState.initial
    )
      return null

    if (
      this.status === quickShopState.submit ||
      this.status === quickShopState.loading
    ) {
      return html`
        <sl-button
          variant="primary:ink"
          class="product-card__atc"
          ?disabled=${this.quickShopVariant.available !== true}
          data-variant-id=${this.quickShopVariant.id}
          ?loading=${this.status === quickShopState.loading}
          @click=${this.handleAddToCartClick}>
          ${this.quickShopVariant.available ? 'Add to Bag' : 'Sold Out'}
        </sl-button>
      `
    }

    if (this.status === quickShopState.confirm) {
      return html`
        <div class="product-card__atc product-card__atc--confirm">
          <span>Item Added to Bag!</span>
        </div>
      `
    }

    if (this.status === quickShopState.error) {
      return html`
        <div class="product-card__atc product-card__atc--confirm product-card__atc--error">
          <span>Something Went Wrong</span>
        </div>
      `
    }

    return null
  }

  render() {
    return html`
      <sl-card
        id="product-card-${this.id}"
        class="product-card"
        style="--padding: 0; --border-width: 0; --sl-shadow-x-small: 0;"
        @mouseenter=${this.handleMouseEnter}
        @mouseleave=${this.handleMouseLeave}
        @option-selected=${this.handleOptionSelection}>
        ${this.renderMedia()}
        <div class="product-card__content" ${ref(this.$productCardContentRef)}>
          <div class="product-card__info">
            ${this.renderProductSwatches()}
          </div>
          <header class="product-card__header">
            <h2 class="product-card__title">
              <a href=${this.activeUrl} class="js-product-link" data-id="${this.id}">${this.activeTitle}</a>
            </h2>
            ${!this.hidePrice ? html`
            <nova-product-card-price
              class="product-card__price"
              .price=${this.activePrice}
              .compareAtPrice=${this.activeCompareAtPrice}
              .isPriceVaries=${this.isPriceVaries}
              .unit=${this.unit}>
            </nova-product-card-price>`: nothing}
          </header>
        </div>
      </sl-card>
    `
  }

  static styles = [
    styles.reset,
    styles.a11y,
    styles.flex,
    styles.helpers,
    styles.typography,
    styles.skeleton,
    css`
      :host {
        display: flex;
        flex-direction: column;
        position: relative;
        container-type: inline-size;
        width: 100%;
        height: 100%;
        position: relative;
      }

      sl-card {
        height: 100%;
      }

      sl-card::part(base) {
        padding: 15px var(--nova-space-xs);
        background: var(--nova-color-white);
        height: 100%;
        --border-radius: 0;
      }

      .product-card__content {
        display: flex;
        flex-direction: column;
        gap: var(--nova-space-sm);
      }

      @media screen and (min-width: 768px) {
        .product-card__content {
          flex-direction: row-reverse;
          justify-content: space-between;
          gap: var(--nova-space-xs);
        }
      }

      .product-card__header {
        display: flex;
        align-items: start;
        gap: var(--nova-space-2xs);
        flex-direction: column;
      }

      .product-card__media {
        position: relative;
        margin-bottom: var(--nova-space-md);
      }

      @media screen and (min-width: 768px) {
        .product-card__media {
          margin-bottom: 15px;
        }
      }

      .product-card .badges {
        position: absolute;
        top: calc(0px - var(--nova-space-2xs));
        left: 0;
      }

      .product-card__image {
        width: 100%;
        aspect-ratio: var(--aspect-ratio-mobile, 155 / 95);
        transition: opacity 0.3s ease-in-out;

        & svg {
          width: 100%;
          height: 100%;
        }
      }

      @media screen and (min-width: 768px) {
        .product-card__image {
          aspect-ratio: var(--aspect-ratio, 1);
        }
      }

      .product-card__image a {
        display: block;
        height: 100%;
      }

      .product-card__image img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
      }

      .product-card__image--primary {
        opacity: 1;
      }

      .product-card__image--secondary {
        position: absolute;
        top: 0;
        left: 0;
        opacity: 0;
      }

      @container (min-width: 300px) {
        .product-card__media--enable-hover:hover .product-card__image--primary {
          opacity: 0;
          position: absolute;
          top: 0;
          left: 0;
        }

        .product-card__media--enable-hover:hover .product-card__image--secondary {
          opacity: 1;
          position: relative;
        }
      }

      .product-card__title {
        flex: 1;
        margin: 0;
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        color: var(--nova-color-grey-600);
      }

      .product-card__title a {
        color: inherit;
      }

      .product-card__price {
        font: var(--nova-body-font);
        letter-spacing: var(--nova-body-letter-spacing);
        text-transform: var(--nova-body-text-transform);
        flex: 0;
        color: var(--nova-color-grey-600);
      }

      .product-card__price dd {
        margin: 0;
      }

      .product-card__swatch-container {
        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        width: fit-content;
        gap: var(--nova-space-xs);
      }

      .product-card__swatch-wrapper {
        display: flex;
        gap: var(--nova-space-2xs);
      }

      .product-card__swatch {
        width: var(--nova-product-color-swatch-size-active);
        height: var(--nova-product-color-swatch-size-active);
        max-width: var(--nova-product-color-swatch-size-active);
        border: 1px solid transparent;
        border-radius: 50%;
        text-indent: -9999px;
        position: relative;
        padding: 0;
        background: transparent;
      }

      .product-card__swatch--active {
        border: 1px solid var(--nova-color-grey-600);
      }

      .product-card__swatch span {
        position: absolute;
        text-indent: 50px;
        overflow: hidden;
        border-radius: 50%;
        display: block;
        background-size: cover;
        width: var(--nova-product-color-swatch-size);
        height: var(--nova-product-color-swatch-size);
        top: 50%;
        left: 50%;
        transform: translate(calc(0px - var(--nova-product-color-swatch-size) / 2), calc(0px - var(--nova-product-color-swatch-size) / 2));
        pointer-events: none;
        color: transparent;
      }

      .product-card__swatch--sold-out::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, transparent calc(50% - 1px), var(--nova-color-foreground) 50%, transparent calc(50% + 1px));
        pointer-events: none;
      }

      .product-card__swatch:nth-child(n+4) {
        display: none;
      }

      .product-card__swatch-container__trigger {
        font: var(--nova-body-utility);
	      letter-spacing: var(--nova-utility-letter-spacing);
	      text-transform: var(--nova-utility-text-transform);
        color: var(--nova-color-grey-400);
      }

      // hack for shoelace and active state on first element
      sl-carousel::part(scroll-container) {
        padding-left: 1px;
      }

      sl-carousel-item {
        width: calc(var(--nova-product-color-swatch-size-active) + 1px);
        height: var(--nova-product-color-swatch-size-active);
      }

      sl-carousel-item button {
        width: var(--nova-product-color-swatch-size-active);
      }

      @media screen and (min-width: 768px) {
        .product-card__swatch--sold-out::after {
          top: 1px;
          left: 1px;
          right: 1px;
          bottom: 1px;
        }
      }

      sl-carousel {
        width: 100%;
        height: 100%;
      }

      sl-carousel::part(base) {
        grid-template-rows: 1fr;
        grid-template-areas: '. slides .';
        gap: 3px;
      }

      sl-carousel::part(navigation-button) {
        padding: 0;
      }

      sl-carousel::part(scroll-container) {
        justify-items: flex-start;
      }

      .product-card__wishlist {
        position: absolute;
        right: var(--nova-space-xs);
        top: var(--nova-space-sm);
      }

      .product-card__wishlist svg {
        pointer-events: none;
      }

      .product-card__wishlist:hover svg {
        fill: var(--nova-brand-secondary);
      }

      .product-card__wishlist:active svg {
        fill: var(--nova-brand-ink);
      }

      .badges {
        display: flex;
        gap: var(--nova-space-2xs);
      }

      .badges__item {
        align-items: flex-start;
        border-radius: var(--nova-border-rounded, 4px);
        background: var(--badge-bg-color, --nova-color-grey-100);
        color: var(--badge-color, --nova-color-grey-600);
        display: flex;
        font: var(--nova-body-utility);
        letter-spacing: 1.4px;
        padding: var(--nova-space-2xs, 5px) 15px;
        text-transform: uppercase;
        pointer-events: none;
        font: var(--nova-body-caption);
        letter-spacing: var(--nova-caption-letter-spacing);
        text-transform: var(--nova-caption-text-transform);
      }

      sl-button[variant='primary:ink'] {
        width: 100%;
      }

      sl-button[variant='primary:ink']::part(base) {
        transition: 0.15s;
      }
      sl-button[variant='primary:ink']::part(base):hover {
        background-color: transparent;
      }
      sl-button[variant='primary:ink'] {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
      }

      sl-button[variant='primary:ink']::part(base) {
        background-color: var(--nova-brand-ink);
        border: none;
        color: white;
      }

      sl-button[variant='primary:ink']::part(base):hover {
        color: white;
        background-color: var(--nova-brand-primary);
      }

      sl-button[disabled]::part(base):hover {
        background-color: var(--nova-brand-neutral);
      }

      .product-card__atc {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        // margin-top: var(--nova-space-sm);
      }

      .product-card__atc span {
        line-height: 40px;
      }

      .product-card__atc--confirm {
        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        background-color: white;
        border-radius: var(--nova-button-radius);
        background-color: var(--nova-brand-secondary);
      }

      .product-card__atc--error {
        background-color: var(--nova-utility-error);
      }


    `,
  ]
}

customElements.define('nova-product-card', NovaProductCard)
